<template>
  <Dialog :open="proModal.isOpen" @update:open="proModal.onClose">
    <DialogContent>
      <DialogHeader>
        <DialogTitle class="flex justify-center items-center flex-col gap-y-4 pb-2">
          <div class="flex items-center gap-x-2 font-bold text-xl">
            {{ title }}
            <Badge variant="premium" class="uppercase text-sm py-1"> pro </Badge>
          </div>
        </DialogTitle>
        <DialogDescription class="text-center pt-2 space-y-2  font-medium">
          <!-- Pro user upgrading to Lifetime: no plan picker (a Pro checkout
               would be refused), just the price breakdown and what changes. -->
          <div v-if="isLifetimeUpgrade" class="space-y-3 text-start text-sm font-normal">
            <div v-if="isLoadingPreview" class="flex justify-center py-6">
              <Icon name="lucide:loader-circle" class="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
            <div v-else-if="previewError" class="py-4 text-destructive">{{ previewError }}</div>
            <template v-else-if="preview">
              <div class="flex justify-between gap-4">
                <span>{{ $t("components.pricingSection.lifetime") }} · {{ $t("components.pricingSection.lifetimeCredits", { credits: formatNumber(LIFETIME_CREDITS, locale) }) }}</span>
                <span class="whitespace-nowrap">{{ formatUsd(preview.priceCents) }}</span>
              </div>
              <div v-if="preview.discountCents > 0" class="flex justify-between gap-4 text-muted-foreground">
                <span>{{ $t("pages.settings.lifetimeUpgradeDiscount", { interval: preview.currentInterval === "year" ? $t("common.annual") : $t("common.monthly") }) }}</span>
                <span class="whitespace-nowrap">−{{ formatUsd(preview.discountCents) }}</span>
              </div>
              <div class="flex justify-between gap-4 border-t pt-3 font-semibold">
                <span>{{ $t("pages.settings.lifetimeUpgradeDueNow") }}</span>
                <span class="whitespace-nowrap">{{ formatUsd(preview.dueNowCents) }}</span>
              </div>
              <p class="text-xs text-muted-foreground">
                {{ preview.currentStatus === "trialing" ? $t("pages.settings.lifetimeUpgradeTrialNote") : $t("pages.settings.lifetimeUpgradeNote") }}
              </p>
              <p class="text-xs text-subtle-foreground">{{ $t("components.pricingSection.lifetimeNote") }}</p>
            </template>
          </div>
          <div v-else-if="proModal.modalType === 'subscription' || proModal.modalType === 'lifetime'">
            <PricingSection
              v-model="isAnnual"
              v-model:plan="plan"
              :hiddenInfo="true"
              :showTrialInfo="user?.canUseTrial !== false"
            />
          </div>
          <div v-else-if="proModal.modalType === 'payPerUse'" class="space-y-4">
            <div class="flex items-center justify-center gap-2">
              <NumberField id="balance" v-model="bonusLimit" :default-value="BONUS_LIMIT_PRICE" :min="BONUS_LIMIT_PRICE" :step="BONUS_LIMIT_PRICE" :format-options="{
                style: 'currency',
                currency: 'USD',
                currencyDisplay: 'code',
                currencySign: 'accounting',
              }">
                <label for="balance">{{ $t("common.add_requests_5_usd", {
                  credits: BONUS_LIMIT,
                  price: BONUS_LIMIT_PRICE,
                }) }}</label>
                <NumberFieldContent>
                  <NumberFieldDecrement />
                  <NumberFieldInput />
                  <NumberFieldIncrement />
                </NumberFieldContent>
              </NumberField>
            </div>
            <div class="text-center font-semibold">
              {{ $t("common.credits") }}: {{ (bonusLimit / BONUS_LIMIT_PRICE * BONUS_LIMIT) }}
            </div>
            <!-- Monthly subscribers who keep topping up are the Lifetime crowd. -->
            <div
              v-if="showLifetimeHint"
              class="flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-start text-[12.5px] font-normal leading-snug text-muted-foreground"
            >
              <Icon name="lucide:infinity" class="mt-0.5 h-3.5 w-3.5 flex-none text-primary" />
              <span class="min-w-0 flex-1">
                {{ $t("common.lifetimeNudge", { price: PRO_LIFETIME_PRICE_USD }) }}
                <button
                  type="button"
                  class="font-semibold text-primary underline-offset-4 hover:underline"
                  @click="proModal.onOpen('lifetime')"
                >
                  {{ $t("common.lifetimeNudgeCta") }}
                </button>
              </span>
            </div>
          </div>

        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button @click="onSubscribe" size="lg" variant="premium" class="w-full" :disabled="isLoading || (isLifetimeUpgrade && (isLoadingPreview || !!previewError))">
          {{ buttonText }}
          <Icon name="lucide:zap" class="w-4 h-4 ms-2 fill-white" />
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { useProModal, type PricingPlan } from '@/store/useProModal';
import { useUser } from '@/composables/useUser';
import { toast } from 'vue-sonner';
import { LIFETIME_CREDITS, PRO_LIFETIME_PRICE_USD } from '~/constants/pricing';

const proModal = useProModal();
const { requireLogin } = useAuthGate();
const { user } = useUser();
const { trackUpsell } = useUpsellTracking();
const isLoading = ref(false);
// Annual is preselected: it is the better deal and the plan we want to lead with.
const isAnnual = ref(true);
const bonusLimit = ref(BONUS_LIMIT_PRICE);
const { t, locale } = useI18n();

// Pay-per-use opened from an "out of credits" moment: preset the smallest
// amount (in $5 steps) that covers what the job was missing.
watch(
  () => [proModal.isOpen, proModal.modalType, proModal.shortfall] as const,
  ([open, type, shortfall]) => {
    if (!open || type !== "payPerUse") return;
    const packs = shortfall ? Math.ceil(shortfall / BONUS_LIMIT) : 1;
    bonusLimit.value = Math.max(1, packs) * BONUS_LIMIT_PRICE;
  },
  { immediate: true }
);

const showLifetimeHint = computed(
  () =>
    user.value?.planState === "premium" &&
    user.value?.billingInterval === "month"
);

// Pro (or trialing) user moving to Lifetime: show the server-computed price
// with the unused-time credit instead of the generic plan picker.
const isLifetimeUpgrade = computed(
  () => proModal.modalType === "lifetime" && !!user.value?.isPro
);
type LifetimePreview = {
  priceCents: number;
  discountCents: number;
  dueNowCents: number;
  currentStatus: "active" | "trialing" | null;
  currentInterval: "month" | "year" | null;
  periodEnd: string | null;
};
const preview = ref<LifetimePreview | null>(null);
const previewError = ref("");
const isLoadingPreview = ref(false);
const formatUsd = (cents: number) => `$${(cents / 100).toFixed(2)}`;
watch(
  () => [proModal.isOpen, isLifetimeUpgrade.value] as const,
  async ([open, upgrade]) => {
    if (!open || !upgrade) return;
    preview.value = null;
    previewError.value = "";
    isLoadingPreview.value = true;
    try {
      preview.value = await $fetch<LifetimePreview>("/api/stripe/lifetime-preview");
    } catch (error: any) {
      const code = error?.data?.data?.code;
      previewError.value =
        code === "ALREADY_LIFETIME"
          ? t("errors.alreadyLifetime")
          : error?.data?.message || t("errors.somethingWentWrong");
    }
    isLoadingPreview.value = false;
  },
  { immediate: true }
);

// "subscription" and "lifetime" share the plan picker; the modal type only
// decides which option starts selected.
const plan = ref<PricingPlan>("pro");
watch(
  () => [proModal.isOpen, proModal.modalType] as const,
  ([open, type]) => {
    if (open) plan.value = type === "lifetime" ? "lifetime" : "pro";
  },
  { immediate: true }
);
const isPlanPicker = computed(
  () => proModal.modalType === "subscription" || proModal.modalType === "lifetime"
);

const title = computed(() => {
  if (!isPlanPicker.value) return t("common.purchaseCredits");
  if (plan.value === "lifetime") return t("common.upgradeToLifetime");
  return user.value?.canUseTrial !== false
    ? t("common.upgradeToPro", { days: TRIAL_DAYS })
    : t("common.upgradeToProNoTrial");
});

const buttonText = computed(() => {
  if (!isPlanPicker.value) return t("buttons.purchase");
  if (plan.value === "lifetime") return t("buttons.buyLifetime");
  return user.value?.canUseTrial !== false
    ? t("buttons.startFreeTrial")
    : t("buttons.upgrade");
});

const onSubscribe = async () => {
  if (!requireLogin()) {
    proModal.onClose();
    return;
  }
  isLoading.value = true;
  try {
    const params = !isPlanPicker.value
      ? `?type=payPerUse&bonusLimit=${bonusLimit.value / BONUS_LIMIT_PRICE * BONUS_LIMIT}`
      : plan.value === "lifetime"
        ? "?type=lifetime"
        : `?billingCycle=${isAnnual.value ? "year" : "month"}`;

    const data = await $fetch(`/api/stripe${params}`);

    if (data) {
      trackUpsell("checkout_open", {
        feature: proModal.feature,
        modal: proModal.modalType,
        plan: !isPlanPicker.value
          ? "credits"
          : plan.value === "lifetime"
            ? "lifetime"
            : isAnnual.value ? "year" : "month",
      });
      // @ts-ignore
      window.location.href = data.url;
    }
  } catch (error: any) {
    console.error(error);
    // 409: the account already has this plan (a Pro checkout while subscribed,
    // or a Lifetime checkout while already on Lifetime).
    if (error?.statusCode === 409 || error?.response?.status === 409) {
      const code = error?.data?.data?.code;
      toast.error(code === "ALREADY_LIFETIME" ? t("errors.alreadyLifetime") : t("errors.alreadySubscribed"));
    } else {
      toast.error(t("errors.somethingWentWrong"));
    }
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped></style>
